import {useCustomQuery, useCustomerId, useProducts} from '@salesforce/commerce-sdk-react'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useMemo} from 'react'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'

/**
 * Unified hook to fetch VickyAI recommendations based on AIModel type
 * Handles all three recommendation types: recommendations, similars, bought-together
 *
 * Performance Strategy:
 * 1. Fetch VickyAI data first (has images, variants, descriptions - from backend cache)
 * 2. Enrich with fresh SFCC data (inventory, promotions, pricing - live data)
 * 3. Use `select` parameter to only fetch missing fields (minimal payload)
 * 4. Don't block initial render - show VickyAI products immediately, enrich in background
 *
 * @param {string} aiModel - Type of recommendation ('recommendations', 'similars', 'bought-together')
 * @param {string} productSku - Product SKU (required for similars and bought-together)
 * @param {string} context - Optional context identifier for debugging (e.g., 'cart', 'pdp')
 * @returns {Object} {products, isLoading, productIds}
 */
export const useVickyAIRecommendations = (aiModel, productSku = null, context = 'unknown') => {
    const {app: appConfig} = getConfig()
    const {site} = useMultiSite()
    const customerId = useCustomerId()
    const isVickyAIEnabled = appConfig?.VICKY_ENABLED === 'true'

    // Determine endpoint and parameters based on AIModel
    // Memoize to prevent creating new config object on every render
    const config = useMemo(() => {

        switch (aiModel?.toLowerCase()) {
            case 'skip':
                // Skip fetching - used when we don't have required data yet
                return {
                    endpointPath: '/recommendations',
                    parameters: {},
                    enabled: false
                }
            case 'similars':
            case 'similar':
                return {
                    endpointPath: '/similars',
                    parameters: {
                        siteId: site?.id,
                        c_sku: productSku,
                        c_brand: 'GRG'
                    },
                    enabled: isVickyAIEnabled && Boolean(productSku && site?.id)
                }
            case 'bought-together':
            case 'boughttogether':
            case 'frequently-bought-together':
                return {
                    endpointPath: '/bought-together',
                    parameters: {
                        siteId: site?.id,
                        c_sku: productSku,
                        c_brand: 'GRG'
                    },
                    enabled: isVickyAIEnabled && Boolean(productSku && site?.id)
                }
            case 'recommendations':
            case 'recommended':
            default:
                return {
                    endpointPath: '/recommendations',
                    parameters: {
                        siteId: site?.id,
                        c_uid: customerId || 'anonymous',
                        c_brand: 'GRG'
                    },
                    enabled: isVickyAIEnabled && Boolean(site?.id)
                }
        }
    }, [aiModel, site?.id, productSku, customerId])

    const {data: recommendations, isLoading} = useCustomQuery(
        {
            options: {
                method: 'GET',
                parameters: config.parameters,
                customApiPathParameters: {
                    apiVersion: 'v1',
                    endpointPath: config.endpointPath,
                    apiName: 'vickyai'
                }
            },
            rawResponse: false
        },
        {
            // Add explicit query key to prevent cache conflicts between different SKU queries
            // Each unique combination of (endpoint, SKU(s), siteId) gets its own cache entry
            queryKey: ['vickyai', config.endpointPath, productSku, site?.id],
            enabled: config.enabled,
            staleTime: 5 * 60 * 1000, // 5 minutes cache
            onError: (error) => {
                console.warn(`VickyAI ${aiModel} fetch failed:`, error)
            }
        }
    )

    // Extract product IDs from VickyAI response
    const dataArray = Array.isArray(recommendations?.data) ? recommendations.data : []
    const productIds = useMemo(() => dataArray.map(item => item.id).join(','), [dataArray])

    // Enrich with fresh promotions, pricing, and inventory from SFCC
    // Using select to only fetch data we don't already have from VickyAI
    const {data: enrichmentData, isLoading: isEnrichmentLoading} = useProducts(
        {
            parameters: {
                ids: productIds,
                perPricebook: true,
                // Only select the fields we need: inventory, promotions, and fresh pricing
                select: '(data.(id,inventory,productPromotions,price,currency))'
            }
        },
        {
            // Don't block initial render - fetch enrichment data after VickyAI data loads
            enabled: Boolean(productIds && !isLoading),
            // Keep stale data while refetching to avoid flickering
            keepPreviousData: true
        }
    )

    // Merge VickyAI data with fresh enrichment data
    const enrichedProducts = useMemo(() => {
        if (!enrichmentData?.data) return dataArray

        // Create a map of enrichment data by product ID for fast lookup
        const enrichmentMap = new Map(
            enrichmentData.data.map(product => [product.id, product])
        )

        // Merge VickyAI products with fresh data
        return dataArray.map(vickyProduct => {
            const freshData = enrichmentMap.get(vickyProduct.id)
            if (!freshData) return vickyProduct

            return {
                ...vickyProduct,
                // Override with fresh data from SFCC
                inventory: freshData.inventory,
                productPromotions: freshData.productPromotions,
                price: freshData.price,
                currency: freshData.currency
            }
        })
    }, [dataArray, enrichmentData])

    return {
        products: enrichedProducts,
        // Only show loading on initial fetch, not during enrichment
        isLoading,
        productIds
    }
}

